import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useUpdateTier } from '@/lib/api/provider-queries'
import { useVouchers } from '@/lib/api/queries'
import type { IntegrationTier, UpdateTierRequest } from '@/lib/api/provider-types'
import type { VouchersParams } from '@/lib/api/client'
import { Loader2, Save, Check } from 'lucide-react'

interface TierMappingTableProps {
  integrationId: number
  tiers: IntegrationTier[]
  eventId: number
}

interface TierEditState {
  token_amount: number
  vouchers: number[]
  auto_credit: boolean
}

export function TierMappingTable({ integrationId, tiers, eventId }: TierMappingTableProps) {
  const [editState, setEditState] = useState<Record<number, TierEditState>>(() => {
    const state: Record<number, TierEditState> = {}
    for (const tier of tiers) {
      state[tier.id] = {
        token_amount: tier.token_amount,
        vouchers: tier.vouchers,
        auto_credit: tier.auto_credit,
      }
    }
    return state
  })
  const [savedTiers, setSavedTiers] = useState<Set<number>>(new Set())

  const updateTier = useUpdateTier()
  const voucherParams: VouchersParams = { event: eventId, page_size: 100 }
  const { data: vouchersData } = useVouchers(voucherParams)
  const vouchers = vouchersData?.results ?? []

  const handleFieldChange = (tierId: number, field: keyof TierEditState, value: unknown) => {
    setSavedTiers(prev => {
      const next = new Set(prev)
      next.delete(tierId)
      return next
    })
    setEditState(prev => ({
      ...prev,
      [tierId]: { ...prev[tierId], [field]: value },
    }))
  }

  const handleVoucherToggle = (tierId: number, voucherId: number) => {
    setEditState(prev => {
      const current = prev[tierId].vouchers
      const next = current.includes(voucherId)
        ? current.filter(v => v !== voucherId)
        : [...current, voucherId]
      return { ...prev, [tierId]: { ...prev[tierId], vouchers: next } }
    })
    setSavedTiers(prev => {
      const next = new Set(prev)
      next.delete(tierId)
      return next
    })
  }

  const handleSaveTier = (tierId: number) => {
    const data: UpdateTierRequest = editState[tierId]
    updateTier.mutate(
      { integrationId, tierId, data },
      { onSuccess: () => setSavedTiers(prev => new Set(prev).add(tierId)) }
    )
  }

  if (tiers.length === 0) {
    return (
      <div className="text-sm text-neutral-500 dark:text-neutral-400 py-4 text-center">
        No tiers found. Sync tiers from the provider first.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-800">
            <th className="text-left py-3 px-2 font-medium text-neutral-600 dark:text-neutral-400">Tier</th>
            <th className="text-left py-3 px-2 font-medium text-neutral-600 dark:text-neutral-400">Token Amount</th>
            <th className="text-left py-3 px-2 font-medium text-neutral-600 dark:text-neutral-400">Vouchers</th>
            <th className="text-left py-3 px-2 font-medium text-neutral-600 dark:text-neutral-400">Auto Credit</th>
            <th className="py-3 px-2"></th>
          </tr>
        </thead>
        <tbody>
          {tiers.map(tier => {
            const state = editState[tier.id]
            if (!state) return null
            const isSaving = updateTier.isPending && updateTier.variables?.tierId === tier.id
            const isSaved = savedTiers.has(tier.id)

            return (
              <tr key={tier.id} className="border-b border-neutral-100 dark:border-neutral-800/50">
                <td className="py-3 px-2">
                  <div className="font-medium text-neutral-900 dark:text-white">{tier.name}</div>
                  {tier.description && (
                    <div className="text-xs text-neutral-500 dark:text-neutral-400">{tier.description}</div>
                  )}
                </td>
                <td className="py-3 px-2">
                  <Input
                    type="number"
                    min={0}
                    value={state.token_amount}
                    onChange={e => handleFieldChange(tier.id, 'token_amount', Number(e.target.value))}
                    className="w-24"
                  />
                </td>
                <td className="py-3 px-2">
                  <div className="flex flex-wrap gap-1">
                    {vouchers.map(v => (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => handleVoucherToggle(tier.id, v.id)}
                        className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                          state.vouchers.includes(v.id)
                            ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300'
                            : 'bg-neutral-50 border-neutral-200 text-neutral-600 dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-400'
                        }`}
                      >
                        {v.name}
                      </button>
                    ))}
                    {vouchers.length === 0 && (
                      <span className="text-xs text-neutral-400">No vouchers</span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={state.auto_credit}
                      onChange={e => handleFieldChange(tier.id, 'auto_credit', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-neutral-200 peer-focus:outline-none rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-neutral-600 peer-checked:bg-blue-600" />
                  </label>
                </td>
                <td className="py-3 px-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleSaveTier(tier.id)}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : isSaved ? (
                      <Check className="w-3.5 h-3.5 text-green-600" />
                    ) : (
                      <Save className="w-3.5 h-3.5" />
                    )}
                  </Button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
